import React, { useEffect, useRef, useState } from 'react';

const DEVICE_SSID = 'LumieresDeJardin';
const STEP_LABELS = ['Branchement', 'Connexion Wi-Fi', 'Détection'];

function StepIndicator({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 22 }}>
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, minWidth: 0 }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12.5, fontWeight: 700,
              background: i <= step ? '#0b8a4f' : '#e7ebee',
              color: i <= step ? '#fff' : '#8a929c',
              border: i === step ? '2px solid #095e37' : 'none',
              transition: 'background .2s ease'
            }}>{i < step ? '✓' : i + 1}</div>
            <span style={{
              fontSize: 10.5, fontWeight: 600, color: i <= step ? '#0d3a57' : '#8a929c',
              textAlign: 'center', whiteSpace: 'nowrap'
            }}>{label}</span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <div style={{ flex: 1, height: 2, margin: '0 4px 16px', background: i < step ? '#0b8a4f' : '#e7ebee' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const NewDeviceWizard = ({
  onCancel,
  unclaimed,
  pairingMac, pairMsg, doPair,
  manualMac, setManualMac, manualError, setManualError,
  normalizeMac, fmtAge,
}) => {
  const [step, setStep] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const autoTriggered = useRef(false);

  // Détection automatique à la dernière étape : dès qu'un seul appareil non
  // appairé est visible, on lance l'appairage tout seul, sans clic supplémentaire.
  // (la fermeture du wizard au succès est déjà gérée par doPair côté parent)
  useEffect(() => {
    if (step !== 2) return;
    if (autoTriggered.current) return;
    if (unclaimed.length === 1) {
      autoTriggered.current = true;
      doPair(normalizeMac(unclaimed[0].identifier));
    }
  }, [step, unclaimed, doPair, normalizeMac]);

  const next = () => setStep(s => Math.min(s + 1, STEP_LABELS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', padding: '4px 2px' }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 19, color: '#0d3a57' }}>🛠 Ajouter un nouvel appareil</h2>
      <StepIndicator step={step} />

      {/* ----- Étape 1 : Branchement ----- */}
      {step === 0 && (
        <div>
          <p style={{ fontSize: 15, lineHeight: 1.6 }}>
            Branchez votre nouvel appareil sur une prise, à un endroit où votre Wi-Fi habituel est bien capté.
          </p>
          <p className="wizard-hint">Le voyant de l'appareil doit clignoter : il est prêt à être configuré.</p>
        </div>
      )}

      {/* ----- Étape 2 : rejoindre le Wi-Fi de l'appareil + configuration ----- */}
      {step === 1 && (
        <div>
          <ol style={{ fontSize: 15, lineHeight: 1.8, paddingLeft: 22, margin: 0 }}>
            <li>
              Votre appareil vient de créer un réseau Wi-Fi temporaire{' '}
              <strong style={{ color: '#0d3a57' }}>{DEVICE_SSID}</strong>.
            </li>
            <li>
              Sur votre téléphone ou votre ordinateur, allez dans le choix des réseaux Wi-Fi et connectez-vous
              au réseau <strong style={{ color: '#0d3a57' }}>{DEVICE_SSID}</strong>.
            </li>
            <li>Une fois connecté, attendez qu'une page de connexion à votre propre Wi-Fi s'ouvre.</li>
            <li>Sur cette page, entrez le nom de votre réseau Wi-Fi et son mot de passe, puis cliquez sur "Enregistrer".</li>
            <li>Votre nouvel appareil va se connecter à votre réseau Wi-Fi.</li>
            <li>Revenez sur cette page et cliquez sur "Suivant".</li>
            <li>Votre nouvel appareil va être automatiquement ajouté à votre espace Lumières de Jardin.</li>
          </ol>

          <p className="wizard-hint" style={{ marginTop: 14 }}>
            La page de connexion ne s'ouvre pas toute seule ? Ouvrez{' '}
            <a href="http://192.168.4.1" target="_blank" rel="noreferrer">http://192.168.4.1</a> dans votre navigateur.
          </p>
        </div>
      )}

      {/* ----- Étape 3 : reconnexion + détection automatique ----- */}
      {step === 2 && (
        <div>
          <p style={{ fontSize: 15, lineHeight: 1.6, marginBottom: 10 }}>
            Je cherche votre nouvel appareil. Patientez un peu, il va être automatiquement ajouté.
          </p>

          {pairMsg ? (
            <div className="callout info">{pairMsg}</div>
          ) : unclaimed.length > 1 ? (
            <>
              <p className="wizard-hint">Plusieurs appareils détectés — choisissez le vôtre :</p>
              <div className="detected-list">
                {unclaimed.map(u => {
                  const mac = normalizeMac(u.identifier);
                  return (
                    <div className="detected-item" key={mac}>
                      <div className="detected-meta">
                        <div className="detected-mac">{mac}</div>
                        <div className="detected-sub">
                          {u.age_sec != null ? `vu il y a ${fmtAge(u.age_sec)}` : ''}
                        </div>
                      </div>
                      <button
                        className="btn btn-primary" onClick={() => doPair(mac)} disabled={pairingMac === mac}
                      >{pairingMac === mac ? '…' : 'Appairer'}</button>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#5b6470', fontSize: 14 }}>
              <span className="fw-spinner" style={{ fontSize: 18 }}>🔄</span>
              Recherche de l'appareil sur votre réseau…
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowManual(s => !s)}
            style={{ marginTop: 16, background: 'none', border: 'none', color: '#1147d1', cursor: 'pointer', fontSize: 12.5, padding: 0, textDecoration: 'underline' }}
          >{showManual ? 'Masquer' : "Ça ne fonctionne pas ? Appairer manuellement"}</button>

          {showManual && (
            <div className="manual">
              <div className="manual-title">Appairage manuel</div>
              <div className="manual-row">
                <input
                  className="row-input"
                  type="text"
                  placeholder="MAC (ex: A1B2C3D4E5F6 ou A1:B2:...)"
                  value={manualMac}
                  onChange={(e) => { setManualError(''); setManualMac(e.target.value); }}
                />
                <button
                  className="btn btn-warning"
                  onClick={() => {
                    const n = normalizeMac(manualMac);
                    if (!n || n.length !== 12) { setManualError('MAC invalide (12 hex).'); return; }
                    doPair(n);
                  }}
                  disabled={!!pairingMac}
                >Appairer</button>
              </div>
              {manualError && <div className="error-text">{manualError}</div>}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 26, display: 'flex', gap: 10, justifyContent: 'space-between' }}>
        <button
          onClick={step === 0 ? onCancel : back}
          style={{ padding: '9px 18px', background: '#eee', color: '#333', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer' }}
        >{step === 0 ? '❌ Annuler' : '← Précédent'}</button>

        {step < STEP_LABELS.length - 1 && (
          <button
            onClick={next}
            style={{ padding: '9px 20px', background: '#0b8a4f', color: '#fff', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >Suivant →</button>
        )}
      </div>
    </div>
  );
};

export default NewDeviceWizard;
